import React, { FC, useState, useCallback, useEffect } from 'react';
import { partition } from 'lodash';

import { LocalItem, LocalVariation } from '~Types';
import { useEntryFinder } from '~Components/EntryFinder';

import View from './View';
import { ItemFragmentOnItem } from '../GridButtons/__queries__';
import useItemModifications from '~Hooks/ItemModifications/useItemModifications';
import { useApolloClient } from '@apollo/client';
import {
  CompleteBongQuery_venue_company_categories_items_courseType,
  CompleteBongQuery_venue_company_categories_items as Item,
} from '~Scenes/POS/Queries/__queries__';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';

export type SelectedUpsales = { id: string; title: string }[];

interface Props {
  item: ItemFragmentOnItem | null;
  onClose: () => void;
  addItem: ({
    id,
    itemTitle,
    variations,
    removedVariations,
    parentId,
  }: {
    id?: string;
    itemTitle?: string;
    variations: LocalVariation[];
    courseType?: CompleteBongQuery_venue_company_categories_items_courseType;
    removedVariations: LocalVariation[];
    parentId?: string;
  }) => { cartItemIds: string[] };
  prevSelected?: {
    variations: LocalVariation[];
    removedVariations: LocalVariation[];
    upsales: LocalItem[];
  };
}

/** The popup component that comes up when the cashier edits modifications of an item */
const ItemModifications: FC<Props> = ({ item, onClose, addItem, prevSelected }) => {
  const findEntryFromId = useEntryFinder();
  const client = useApolloClient();

  // Previously selected variations
  const prevSelectedVariationIds = prevSelected?.variations.map((v) => v.id) ?? [];

  // Previously removed variations
  const prevRemovedVariationIds = prevSelected?.removedVariations?.map((v) => v.id) ?? [];

  const itemModifications = useItemModifications(
    item?.modificationGroups ?? [],
    prevSelectedVariationIds,
    prevRemovedVariationIds,
  );

  // Previously selected upsales
  const prevSelectedUpsales =
    prevSelected?.upsales.map((upsale) => ({ id: upsale.itemId, title: upsale.title })) ?? [];

  const [selectedUpsales, setSelectedUpsales] = useState<SelectedUpsales>(prevSelectedUpsales);
  const upsaleIsSelected = (itemId: string) => !!selectedUpsales.find((i) => i.id === itemId);

  // State to ensure that when the modal is mounted, no accidental initial clicks are made
  const [clickPreventionActive, setClickPreventionActive] = useState<boolean>(true);

  useEffect(() => {
    const timer = setTimeout(() => setClickPreventionActive(false), 200);
    return () => clearTimeout(timer);
  }, []);

  const hasModificationGroups = !!item?.modificationGroups?.length;
  const hasUpsales = !!item?.upsales?.length;

  const onSelect = useCallback(
    ({
      variations,
      removedVariations,
      upsaleItems,
    }: {
      variations: LocalVariation[];
      removedVariations: LocalVariation[];
      upsaleItems: Array<{ id: string; title: string }>;
    }) => {
      // Split variations up into "is actually an item" and "is not an item"
      const [withItem, withoutItem] = partition(variations, (vari) => {
        // If it does not have an item config, return
        if (!vari.asItem) {
          return false;
        }

        // Find this item in the menu. If it does not exist in the menu,
        // dont treat this variation as an item
        const entry = findEntryFromId(vari.asItem.id);

        // Return true if there is an entry and the price is the same as the
        // price of the variation. This prevents cases where the price in the
        // UI is different than the price on the menu.
        // Also allows for stuff like pommes to be cheaper when
        // bought with a burger
        return entry && entry.price === vari.price;
      });

      // Add our item, with the variations that are items removed
      // If item has children items (upsells/variations as items), set parentId to "parent"
      const { cartItemIds } = addItem({
        removedVariations,
        variations: withoutItem,
        parentId: withItem.length > 0 || upsaleItems.length > 0 ? 'parent' : undefined,
      });

      // Add our "as item" variations to the cart
      withItem.forEach((vari) => {
        addItem({
          id: vari.asItem?.id,
          itemTitle: vari.name,
          removedVariations: [],
          variations: [],
          parentId: cartItemIds[0],
        });
      });

      // Add any upsale items to cart
      upsaleItems.forEach((upsaleItem) => {
        const fullUpsaleItemData = client.readFragment<Item>({
          id: `Item${upsaleItem.id}`,
          fragment: ITEM_FRAGMENT,
        });

        addItem({
          id: upsaleItem.id,
          itemTitle: upsaleItem.title,
          courseType: fullUpsaleItemData?.courseType,
          removedVariations: [],
          variations: [],
          parentId: cartItemIds[0],
        });
      });

      onClose();
    },
    [],
  );

  // useEffect()
  const onSelectUpsale = (itemId: string, itemTitle?: string) => {
    if (upsaleIsSelected(itemId)) {
      setSelectedUpsales(selectedUpsales.filter((i) => i.id !== itemId));
    } else {
      setSelectedUpsales([...selectedUpsales, { id: itemId, title: itemTitle || '' }]);
    }
  };

  const addItemToCart = () => {
    onSelect({
      variations: itemModifications.selectedVariations,
      removedVariations: itemModifications.removedVariations,
      upsaleItems: selectedUpsales,
    });
  };

  const upsellGroups = item?.upsales
    .filter((u) => !u.hideInPOS)
    .map((upsell) => {
      return {
        ...upsell,
        items: upsell.items.filter((upsellItem) => findEntryFromId(upsellItem.id)),
      };
    });

  return (
    <>
      {(hasModificationGroups || hasUpsales) && item && (
        <View
          modificationGroups={itemModifications.modificationGroups}
          upsellGroups={upsellGroups}
          selectedVariations={itemModifications?.selectedVariations || []}
          selectedUpsales={selectedUpsales}
          onSelectVariation={(itemId) =>
            !clickPreventionActive && itemModifications.onSelectVariation(itemId)
          }
          onSelectUpsale={(itemId, itemTitle?) =>
            !clickPreventionActive && onSelectUpsale(itemId, itemTitle)
          }
          addItemToCart={addItemToCart}
          isEditing={Boolean(prevSelected)}
          onClose={onClose}
        />
      )}
    </>
  );
};

export default React.memo(ItemModifications);
